import React, { useState, useCallback, useRef } from 'react';
import { X, Settings } from 'lucide-react';
import { SystemPromptEditor } from './SystemPromptEditor';

interface SystemPromptDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SystemPromptDrawer({ isOpen, onClose }: SystemPromptDrawerProps) {
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const saveRef = useRef<(() => void) | null>(null);

  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setShowCloseWarning(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleSaveAndClose = useCallback(() => {
    saveRef.current?.();
    setShowCloseWarning(false);
    onClose();
  }, [onClose]);

  const handleDiscard = useCallback(() => {
    setShowCloseWarning(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="absolute inset-0 z-[61] bg-zinc-900 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Settings size={14} className="text-zinc-400" />
          <span className="text-sm font-medium text-zinc-200">
            System Prompt
            {isDirty && <span className="text-yellow-400 ml-1">●</span>}
          </span>
          <span className="text-xs text-zinc-500">— shared across all screens</span>
        </div>
        <button
          onClick={handleRequestClose}
          className="p-1 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 rounded transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden">
        <SystemPromptEditor
          onDirtyChange={setIsDirty}
          saveRef={saveRef}
        />
      </div>

      {/* VS Code-style save dialog */}
      {showCloseWarning && (
        <>
          {/* Backdrop */}
          <div className="absolute inset-0 z-[70] bg-black/50" />

          {/* Dialog */}
          <div className="absolute inset-0 z-[71] flex items-center justify-center">
            <div className="bg-zinc-800 border border-zinc-600 rounded-lg shadow-2xl w-[340px] overflow-hidden">
              <div className="px-5 pt-5 pb-3">
                <h3 className="text-sm font-medium text-zinc-200">
                  Do you want to save changes?
                </h3>
                <p className="text-xs text-zinc-400 mt-1.5">
                  Your changes will be lost if you don't save them.
                </p>
              </div>
              <div className="flex items-center justify-end gap-2 px-5 pb-4">
                <button
                  onClick={handleSaveAndClose}
                  className="px-4 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleDiscard}
                  className="px-4 py-1.5 text-xs font-medium border border-zinc-600 text-zinc-300 hover:bg-zinc-700 rounded transition-colors"
                >
                  Don't Save
                </button>
                <button
                  onClick={() => setShowCloseWarning(false)}
                  className="px-4 py-1.5 text-xs font-medium border border-zinc-600 text-zinc-300 hover:bg-zinc-700 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
